import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import HomePage from './pages/HomePage';
import AgenciesPage from './pages/AgenciesPage';
import AgencyListings from './pages/AgencyListings';
import ComplexesPage from './pages/ComplexesPage';
import ComplexDetail from './pages/ComplexDetail';
import ListingDetail from './pages/ListingDetail';
import AgencyLogin from './pages/AgencyLogin';
import AgencyDashboard from './pages/AgencyDashboard';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import CreateListing from './pages/CreateListing';
import Cabinet from './pages/Cabinet';
import AuthCallback from './pages/AuthCallback';
import AgentCabinet from './pages/AgentCabinet';
import AgencyOwnerCabinet from './pages/AgencyOwnerCabinet';
// Mock verileri sadece Users (Giriş) için tutuyoruz, diğerlerini iptal ettik
import { mockUsers } from './data/mockData';
import { fetchListings, fetchAgencies, fetchComplexes } from './lib/supabase';
import './App.css';

// Initialize data from localStorage with fallback to EMPTY ARRAY [] (No Mock Data)
const getInitialData = (key, defaultData) => {
  const saved = localStorage.getItem(key);
  if (saved) return JSON.parse(saved);
  // ARTIK VARSAYILAN OLARAK BOŞ DİZİ [] DÖNDÜRÜYORUZ, SAHTE VERİ DEĞİL.
  return defaultData;
};

// Transform Supabase data to app format
const transformListingFromDB = (dbListing) => ({
  id: dbListing.id,
  price: dbListing.price,
  category: dbListing.category,
  location: dbListing.location,
  type: dbListing.type,
  rooms: dbListing.rooms,
  area: dbListing.area,
  floor: dbListing.floor,
  totalFloors: dbListing.total_floors,
  images: dbListing.images || [],
  image: dbListing.images?.[0] || null,
  isVip: dbListing.is_vip,
  isAgency: dbListing.is_agency,
  agencyId: dbListing.agency_id,
  agencyName: dbListing.agency_name,
  userId: dbListing.user_id,
  ownerType: dbListing.owner_type,
  description: dbListing.description,
  phone: dbListing.owner_phone,
  createdAt: dbListing.created_at
});

// Transform Supabase complex data to app format
const transformComplexFromDB = (dbComplex) => {
  return {
    id: dbComplex.id,
    name: dbComplex.name || '',
    location: dbComplex.location || '',
    developer: dbComplex.developer || '',
    totalApartments: dbComplex.total_apartments ?? dbComplex.totalApartments ?? 0,
    priceFrom: dbComplex.price_from ?? dbComplex.priceFrom ?? 0,
    image: dbComplex.image || '',
    description: dbComplex.description || '',
    amenities: Array.isArray(dbComplex.amenities) ? dbComplex.amenities : [],
    status: dbComplex.status || 'Hazır',
    isActive: dbComplex.is_active ?? dbComplex.isActive ?? true
  };
};

// Transform Supabase agency data to app format
const transformAgencyFromDB = (dbAgency) => {
  return {
    id: dbAgency.id,
    name: dbAgency.name || '',
    accessCode: dbAgency.access_code ?? dbAgency.accessCode ?? '',
    phone: dbAgency.phone || '',
    email: dbAgency.email || '',
    address: dbAgency.address || '',
    logo: dbAgency.logo || '',
    description: dbAgency.description || '',
    listingsCount: dbAgency.listings_count ?? dbAgency.listingsCount ?? 0,
    isActive: dbAgency.is_active ?? dbAgency.isActive ?? true,
    createdAt: dbAgency.created_at || dbAgency.createdAt || ''
  };
};

function App() {
  // BAŞLANGIÇ VERİLERİNİ ARTIK BOŞ [] OLARAK AYARLADIK
  const [listings, setListings] = useState(() => getInitialData('mydom_listings', []));
  const [agencies, setAgencies] = useState(() => getInitialData('mydom_agencies', []));
  const [complexes, setComplexes] = useState(() => getInitialData('mydom_complexes', []));
  const [users, setUsers] = useState(() => getInitialData('mydom_users', mockUsers)); // Kullanıcılar şimdilik kalsın (Admin girişi için)
  
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(() => {
    const saved = localStorage.getItem('mydom_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [currentAgency, setCurrentAgency] = useState(() => {
    const saved = localStorage.getItem('mydom_agency');
    return saved ? JSON.parse(saved) : null;
  });

  // Load data from Supabase on mount
  const loadDataFromSupabase = useCallback(async () => {
    try {
      console.log('Loading data from Supabase...');
      
      // Fetch listings from Supabase
      const dbListings = await fetchListings();
      console.log('Fetched listings from Supabase:', dbListings);
      
      if (dbListings) {
        const transformedListings = dbListings.map(transformListingFromDB);
        // ARTIK MOCK DATA İLE BİRLEŞTİRMİYORUZ. SADECE DB VERİSİ.
        setListings(transformedListings);
        localStorage.setItem('mydom_listings', JSON.stringify(transformedListings));
      } else {
        setListings([]);
      }

      // Fetch agencies from Supabase
      const dbAgencies = await fetchAgencies();
      if (dbAgencies) {
        const transformedAgencies = dbAgencies.map(transformAgencyFromDB);
        setAgencies(transformedAgencies);
        localStorage.setItem('mydom_agencies', JSON.stringify(transformedAgencies));
      } else {
        setAgencies([]);
      }

      // Fetch complexes from Supabase  
      const dbComplexes = await fetchComplexes();
      if (dbComplexes) {
        const transformedComplexes = dbComplexes.map(transformComplexFromDB);
        setComplexes(transformedComplexes);
        localStorage.setItem('mydom_complexes', JSON.stringify(transformedComplexes));
      } else {
        setComplexes([]);
      }

    } catch (error) {
      console.error('Error loading data from Supabase:', error);
      // Hata olursa localStorage'daki eski verilerle devam et, ama mock yükleme
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDataFromSupabase();
  }, [loadDataFromSupabase]);

  // Refresh listings from database
  const refreshListings = async () => {
    try {
      const dbListings = await fetchListings();
      if (dbListings) {
        const transformedListings = dbListings.map(transformListingFromDB);
        // BURADA DA MOCK DATA İPTAL EDİLDİ
        setListings(transformedListings);
        localStorage.setItem('mydom_listings', JSON.stringify(transformedListings));
      }
    } catch (error) {
      console.error('Error refreshing listings:', error);
    }
  };

  const updateListings = (data) => {
    setListings(data);
    localStorage.setItem('mydom_listings', JSON.stringify(data));
  };

  const updateAgencies = (data) => {
    setAgencies(data);
    localStorage.setItem('mydom_agencies', JSON.stringify(data));
  };

  const updateComplexes = (data) => {
    setComplexes(data);
    localStorage.setItem('mydom_complexes', JSON.stringify(data));
  };

  const updateUsers = (data) => {
    setUsers(data);
    localStorage.setItem('mydom_users', JSON.stringify(data));
  };

  const handleUserLogin = (user) => {
    setCurrentUser(user);
    localStorage.setItem('mydom_user', JSON.stringify(user));
  };

  const handleUserLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('mydom_user');
  };

  const handleAgencyLogin = (agency) => {
    setCurrentAgency(agency);
    localStorage.setItem('mydom_agency', JSON.stringify(agency));
  };

  const handleAgencyLogout = () => {
    setCurrentAgency(null);
    localStorage.removeItem('mydom_agency');
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={
          <HomePage 
            listings={listings}
            agencies={agencies}
            user={currentUser}
            users={users}
            onLogin={handleUserLogin}
            onLogout={handleUserLogout}
            onUpdateUsers={updateUsers}
          />
        } />
        <Route path="/agencies" element={
          <AgenciesPage 
            agencies={agencies}
            listings={listings}
            user={currentUser}
            users={users}
            onLogin={handleUserLogin}
            onLogout={handleUserLogout}
            onUpdateUsers={updateUsers}
          />
        } />
        <Route path="/agencies/:id" element={
          <AgencyListings 
            agencies={agencies}
            listings={listings}
          />
        } />
        <Route path="/complexes" element={
          <ComplexesPage 
            complexes={complexes}
            user={currentUser}
            users={users}
            agencies={agencies}
            onLogin={handleUserLogin}
            onLogout={handleUserLogout}
            onUpdateUsers={updateUsers}
          />
        } />
        <Route path="/complexes/:id" element={
          <ComplexDetail 
            complexes={complexes}
            listings={listings}
          />
        } />
        <Route path="/listings/:id" element={
          <ListingDetail 
            listings={listings}
            agencies={agencies}
            users={users}
          />
        } />
        <Route path="/create-listing" element={
          currentUser ? (
            <CreateListing 
              user={currentUser}
              listings={listings}
              agencies={agencies}
              onUpdateListings={updateListings}
              onRefreshListings={refreshListings}
            />
          ) : (
            <Navigate to="/" replace />
          )
        } />
        <Route path="/cabinet" element={
          currentUser ? (
            <Cabinet
              user={currentUser}
              listings={listings}
              agencies={agencies}
              onUpdateListings={updateListings}
              onRefreshListings={refreshListings}
            />
          ) : (
            <Navigate to="/" replace />
          )
        } />
        <Route path="/agent-cabinet" element={
          currentUser?.isAgent ? (
            <AgentCabinet
              user={currentUser}
              agencies={agencies}
              onRefreshListings={refreshListings}
            />
          ) : (
            <Navigate to="/" replace />
          )
        } />
        <Route path="/agency-owner-cabinet" element={
          currentUser?.isAgencyOwner ? (
            <AgencyOwnerCabinet
              user={currentUser}
              agencies={agencies}
              onRefreshListings={refreshListings}
            />
          ) : (
            <Navigate to="/" replace />
          )
        } />
        <Route path="/agency/login" element={
          <AgencyLogin 
            agencies={agencies}
            onAgencyLogin={handleAgencyLogin}
          />
        } />
        <Route path="/agency/dashboard" element={
          currentAgency ? (
            <AgencyDashboard 
              agency={currentAgency}
              listings={listings}
              onUpdateListings={updateListings}
              onLogout={handleAgencyLogout}
            />
          ) : (
            <Navigate to="/agency/login" replace />
          )
        } />
        <Route path="/admin" element={
          currentUser?.isAdmin ? (
            <Navigate to="/admin/dashboard" replace />
          ) : (
            <AdminLogin onLogin={handleUserLogin} />
          )
        } />
        <Route path="/admin/dashboard" element={
          currentUser?.isAdmin ? (
            <AdminDashboard 
              listings={listings}
              agencies={agencies}
              complexes={complexes}
              onUpdateListings={updateListings}
              onUpdateAgencies={updateAgencies}
              onUpdateComplexes={updateComplexes}
              onLogout={handleUserLogout}
            />
          ) : (
            <Navigate to="/admin" replace />
          )
        } />
        <Route path="/auth/callback" element={
          <AuthCallback onLogin={handleUserLogin} />
        } />
      </Routes>
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;
